import {
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
  Theme,
} from '@mui/material';
import {
  tannerBlack,
  tannerGray,
  tannerSemantic,
  tannerWhite,
} from '../colors';

type MuiTextField = {
  styleOverrides?: ComponentsOverrides<Theme>['MuiTextField'];
  defaultProps?: ComponentsProps['MuiTextField'];
  variants?: ComponentsVariants['MuiTextField'];
};

export const MuiTextField: MuiTextField = {
  defaultProps: {
    variant: 'filled',
  },
  variants: [
    {
      props: { variant: 'filled' },
      style: {
        '& .MuiCharacterCount': {
          flex: 'none',
          paddingLeft: '8px',
        },
        '&.MuiTextField-multiline .MuiInputLabel-root': {
          display: 'block',
          width: '100%',
          maxWidth: '100%',
          padding: '20px 16px 0px',
          textOverflow: 'initial',
          whiteSpace: 'initial',
          overflow: 'initial',
          transform: 'translate(0, 0) scale(1)',
          '&.MuiInputLabel-shrink': {
            display: '-webkit-box',
            width: '123.25%',
            maxWidth: '123.25%',
            transform: 'translate(4px, -4px) scale(0.8)',
            boxOrient: 'vertical',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          },
        },
        '&.MuiTextField-multiline.MuiTextField-multiline-clamp-': {
          '&1': {
            '.MuiInputLabel-shrink': {
              lineClamp: '1',
              WebkitLineClamp: '1',
            },
          },
          '&2': {
            '.MuiInputLabel-shrink': {
              lineClamp: '2',
              WebkitLineClamp: '2',
            },
            '.MuiFilledInput-root': {
              paddingTop: '41px',
            },
          },
          '&3': {
            '.MuiInputLabel-shrink': {
              lineClamp: '3',
              WebkitLineClamp: '3',
            },
            '.MuiFilledInput-root': {
              paddingTop: '57px',
            },
          },
        },
        '& input[readonly]': {
          borderColor: tannerGray['100'],
        },
        '&.MuiTextField-readonly': {
          '& .MuiInputBase-root': {
            borderWidth: 1,
            pointerEvents: 'none',
            borderStyle: 'solid',
            borderColor: tannerGray['200'],
            overflow: 'hidden',
            borderRadius: 3,
            transition: 'border-color .2s, box-shadow .2s',
            ':hover': {
              borderColor: tannerGray['200'],
              '&.Mui-disabled': {
                borderColor: tannerGray['200'],
              },
            },
            '&.Mui-focused': {
              backgroundColor: tannerWhite,
              borderColor: tannerGray['200'],
              boxShadow: `none`,
            },
          },
          '.MuiIconButton-root': {
            color: tannerGray['400'],
            fontSize: '12px',
            pointerEvents: 'visible',
            cursor: 'auto',
            height: '20px',
            '&:hover': {
              backgroundColor: tannerWhite,
            },
          },
        },
        '& .MuiInputBase-root': {
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: tannerGray['500'],
          overflow: 'hidden',
          borderRadius: 3,
          transition: 'border-color .2s, box-shadow .2s',
          minHeight: '56px',
          '&:hover': {
            borderColor: '#000',
            '&.Mui-disabled': {
              borderColor: tannerGray['300'],
            },
            '&.Mui-error': {
              borderColor: tannerSemantic.error.color,
            },
          },
          '& input[type="number"], & input[type="search"]': {
            WebkitAppearance: 'textfield',
            MozAppearance: 'textfield',
            appearance: 'textfield',
          },
          '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button':
            {
              WebkitAppearance: 'none',
            },
          '& input[type="search"]::-webkit-search-decoration, & input[type="search"]::-webkit-search-cancel-button, & input[type="search"]::-webkit-search-results-button, & input[type="search"]::-webkit-search-results-decoration':
            {
              WebkitAppearance: 'none',
            },
          '&.Mui-focused': {
            backgroundColor: tannerWhite,
            borderColor: '#000',
            boxShadow: `0px 0px 0px 2px ${tannerGray['900']}33`,
          },
          '&.Mui-error': {
            borderColor: tannerSemantic.error.color,
          },
          '&.Mui-error.Mui-focused': {
            boxShadow: `${tannerSemantic.error.background} 0 0 0 2px`,
            borderColor: tannerSemantic.error.color,
          },
        },
        '& .MuiInputBase-multiline': {
          minHeight: '56px',
          alignItems: 'normal',
          '.MuiIconButton-root': {
            alignItems: 'start',
            marginTop: '-12px',
          },
        },
      },
    },
    {
      props: { variant: 'outlined' },
      style: {
        '& .MuiInputBase-root': {
          padding: '10px',
          minHeight: '56px',
          '&.MuiInputBase-sizeSmall': {
            minHeight: '40px',
          },
          '&.Mui-focused fieldset': {
            borderColor: tannerBlack,
            borderWidth: 1,
            boxShadow: `0px 0px 0px 2px ${tannerGray['900']}33`,
          },
          '&.Mui-error fieldset': {
            borderColor: tannerSemantic.error.color,
          },
          '&.Mui-error.Mui-focused fieldset': {
            boxShadow: `${tannerSemantic.error.background} 0 0 0 2px`,
            borderColor: tannerSemantic.error.color,
          },
        },
        '& fieldset': {
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: tannerGray['500'],
          overflow: 'hidden',
          borderRadius: 3,
          transition: 'border-color .2s, box-shadow .2s',
        },
        '& fieldset legend': {
          fontSize: '0.65em',
        },
      },
    },
  ],
};
